import React, { useEffect, useMemo, useState } from 'react';
import {
  sendCustomerEmail,
  SendCustomerEmailDryRun,
  SendCustomerEmailPayload,
} from '@onesunday/api-client';

export interface EmailScope {
  source?: string;
  eventDate?: string;
  createdFrom?: string;
  createdTo?: string;
  description: string;
}

interface Props {
  open: boolean;
  scope: EmailScope;
  onClose: () => void;
  onSent: () => void;
}

interface Template {
  id: string;
  label: string;
  subject: string;
  body: string;
}

const TEMPLATES: Template[] = [
  {
    id: 'address-correction',
    label: 'Address correction',
    subject: '{{brandName}} — Pickup address correction for {{orderId}}',
    body: `Hi {{name}},

A quick correction to your {{brandName}} order {{orderId}} for {{eventDate}}:

The pickup address is {{pickupAddress}}.
Pickup hours: {{pickupHours}}.
Contact: {{pickupContact}}.

Apologies for the confusion — see you soon.

— {{brandName}}`,
  },
  {
    id: 'pickup-reminder',
    label: 'Pickup reminder',
    subject: '{{brandName}} — Reminder: pickup for {{orderId}} on {{eventDate}}',
    body: `Hi {{name}},

Quick reminder for your {{brandName}} order {{orderId}}:

Pickup is {{eventDate}} at {{pickupAddress}}.
Hours: {{pickupHours}}.
Contact on the day: {{pickupContact}}.

See you then.

— {{brandName}}`,
  },
];

const VARIABLE_HINTS = [
  '{{name}}',
  '{{orderId}}',
  '{{eventDate}}',
  '{{pickupAddress}}',
  '{{pickupHours}}',
  '{{pickupContact}}',
  '{{brandName}}',
];

export const CustomerEmailModal: React.FC<Props> = ({ open, scope, onClose, onSent }) => {
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState<SendCustomerEmailDryRun | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset every time the modal opens — don't carry stale state across uses.
  useEffect(() => {
    if (open) {
      setSubject('');
      setBodyText('');
      setPreview(null);
      setError(null);
      setPreviewing(false);
      setSending(false);
    }
  }, [open]);

  const scopePayload = useMemo<SendCustomerEmailPayload>(
    () => ({
      subject,
      bodyText,
      source: scope.source,
      eventDate: scope.eventDate,
      createdFrom: scope.createdFrom,
      createdTo: scope.createdTo,
    }),
    [subject, bodyText, scope],
  );

  if (!open) return null;

  const applyTemplate = (t: Template) => {
    setSubject(t.subject);
    setBodyText(t.body);
    setPreview(null);
    setError(null);
  };

  const runPreview = async () => {
    if (!subject.trim() || !bodyText.trim()) {
      setError('Subject and body are required.');
      return;
    }
    setPreviewing(true);
    setError(null);
    try {
      const res = await sendCustomerEmail({ ...scopePayload, dryRun: true });
      if (!('dryRun' in res) || !res.dryRun) {
        setError('Unexpected response from server.');
        return;
      }
      setPreview(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const runSend = async () => {
    if (!preview) return;
    if (preview.eligible === 0) {
      setError('No eligible recipients.');
      return;
    }
    const ok = window.confirm(
      `Send "${preview.samplePreview?.subject || subject}" to ${preview.eligible} customer${preview.eligible === 1 ? '' : 's'}?\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    setSending(true);
    setError(null);
    try {
      const res = await sendCustomerEmail(scopePayload);
      if ('sent' in res) {
        window.alert(`Done.\n\nSent: ${res.sent}\nFailed: ${res.failed}\nSkipped: ${res.skipped}`);
        onSent();
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-stone-200">
          <div>
            <h2 className="text-base font-semibold text-stone-900">Send customer email</h2>
            <p className="text-xs text-stone-500 mt-0.5">Targeting: {scope.description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-700 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-stone-600 uppercase tracking-wider mb-2">
              Quick templates
            </div>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyTemplate(t)}
                  className="text-xs px-3 py-1.5 rounded-md border border-stone-300 bg-stone-50 hover:bg-stone-100 text-stone-700 font-medium transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setPreview(null);
              }}
              placeholder="e.g. Important update about your order"
              className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-stone-600 uppercase tracking-wider mb-1.5">
              Body
            </label>
            <textarea
              value={bodyText}
              onChange={(e) => {
                setBodyText(e.target.value);
                setPreview(null);
              }}
              rows={10}
              placeholder="Hi {{name}},&#10;&#10;Your message here..."
              className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm font-mono focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            />
            <div className="text-xs text-stone-500 mt-1.5">
              Variables (replaced per-customer):{' '}
              {VARIABLE_HINTS.map((v, i) => (
                <React.Fragment key={v}>
                  <code className="px-1 py-0.5 bg-stone-100 rounded text-stone-700">{v}</code>
                  {i < VARIABLE_HINTS.length - 1 ? ' ' : ''}
                </React.Fragment>
              ))}
            </div>
          </div>

          {preview && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3">
              <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                Preview · rendered for {preview.sample[0]?.name || preview.sample[0]?.email || 'first recipient'}
              </div>
              <div>
                <div className="text-[11px] text-stone-600 uppercase tracking-wider">Subject</div>
                <div className="text-sm font-medium text-stone-900">{preview.samplePreview?.subject}</div>
              </div>
              <div>
                <div className="text-[11px] text-stone-600 uppercase tracking-wider">Body</div>
                <pre className="text-sm text-stone-800 whitespace-pre-wrap font-sans bg-white rounded p-2 border border-amber-200">
                  {preview.samplePreview?.text}
                </pre>
              </div>
              <div className="border-t border-amber-200 pt-3">
                <div className="text-sm text-stone-800">
                  <strong>{preview.eligible}</strong> recipient{preview.eligible === 1 ? '' : 's'}
                  {preview.matched !== preview.eligible
                    ? ` (${preview.matched - preview.eligible} skipped — no valid email)`
                    : ''}
                </div>
                {preview.sample.length > 0 && (
                  <details className="text-xs text-stone-600 mt-1">
                    <summary className="cursor-pointer hover:text-stone-900">Show sample</summary>
                    <ul className="mt-1 space-y-0.5 font-mono">
                      {preview.sample.map((s) => (
                        <li key={s.orderId}>
                          {s.orderId} — {s.name || '(no name)'} &lt;{s.email}&gt;
                        </li>
                      ))}
                      {preview.eligible > preview.sample.length && (
                        <li>…and {preview.eligible - preview.sample.length} more</li>
                      )}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-stone-200 bg-stone-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
          >
            Cancel
          </button>
          {!preview ? (
            <button
              type="button"
              onClick={runPreview}
              disabled={previewing || !subject.trim() || !bodyText.trim()}
              className="px-4 py-2 text-sm font-medium rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {previewing ? 'Loading preview…' : 'Preview & verify'}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={runSend}
                disabled={sending || preview.eligible === 0}
                className="px-4 py-2 text-sm font-medium rounded-md bg-amber-600 hover:bg-amber-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending
                  ? 'Sending…'
                  : preview.eligible === 0
                    ? 'No recipients'
                    : `Send to ${preview.eligible}`}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};
